/**
 * The Ticket Management list's rules, pure: which tab opens first, what each tab contains, and
 * how a queue is grouped and ordered. Kept out of the view so they can be tested without a DOM.
 */
import type { PeopleTicket, TicketQueue, TicketStatus } from '../../types/peopleTickets';

export const MY_TICKETS = 'mine';
export const ALL_TICKETS = 'all';
export type QueueTab = typeof MY_TICKETS | typeof ALL_TICKETS | TicketQueue;

/** Tab names (David, Oct 1 2026). The queue VALUE stays `Workplace`; only its label is Office. */
export const QUEUE_LABELS: Record<TicketQueue, string> = {
  'Business Partnering': 'Business Partnering',
  'Payroll & Benefits': 'Payroll & Benefits',
  Workplace: 'Office',
};

export function queueLabel(queue: TicketQueue | null): string {
  return queue ? QUEUE_LABELS[queue] ?? queue : '';
}

export const OPEN_STATUSES: TicketStatus[] = ['Submitted', 'In Progress', 'Waiting for Employee'];
const STATUS_ORDER: TicketStatus[] = ['Submitted', 'In Progress', 'Waiting for Employee', 'Resolved', 'Cancelled'];

/**
 * Opens on My Tickets for anyone Ticket Routing names in a queue (they will have tickets of their
 * own), otherwise on All Tickets.
 */
export function defaultQueueTab(myQueues: TicketQueue[]): QueueTab {
  return myQueues.length ? MY_TICKETS : ALL_TICKETS;
}

/** My Tickets = assigned to me. The queue tabs already show each whole queue. */
export function inTab(ticket: PeopleTicket, tab: QueueTab, myEmail: string): boolean {
  if (tab === ALL_TICKETS) return true;
  if (tab === MY_TICKETS) return ticket.assignee?.email === myEmail;
  return ticket.queue === tab;
}

/** Urgent first, then how far along it is, then longest-waiting first. */
export function compareTickets(a: PeopleTicket, b: PeopleTicket): number {
  if (a.priority !== b.priority) return a.priority === 'Urgent' ? -1 : 1;
  const stage = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
  if (stage) return stage;
  return (a.submittedAt ? Date.parse(a.submittedAt) : 0) - (b.submittedAt ? Date.parse(b.submittedAt) : 0);
}

export const UNASSIGNED = '';

export interface AssigneeGroup {
  email: string;
  name: string;
  tickets: PeopleTicket[];
}

/**
 * One group per assignee, the viewer's own first, then alphabetical — and UNASSIGNED FIRST OF
 * ALL. Routing leaves a ticket unassigned only when a rule says nobody owns it yet or the routing
 * table could not be read, and both are exactly the tickets somebody has to see before anything
 * else; at the bottom of a long list they would sit unnoticed.
 */
export function groupByAssignee(tickets: PeopleTicket[], myEmail: string): AssigneeGroup[] {
  const groups = new Map<string, AssigneeGroup>();
  for (const t of tickets) {
    const email = t.assignee?.email ?? UNASSIGNED;
    const group = groups.get(email) ?? { email, name: t.assignee?.name ?? 'Unassigned', tickets: [] };
    group.tickets.push(t);
    groups.set(email, group);
  }
  const rank = (g: AssigneeGroup) => (g.email === UNASSIGNED ? 0 : g.email === myEmail ? 1 : 2);
  return [...groups.values()]
    .map((g) => ({ ...g, tickets: [...g.tickets].sort(compareTickets) }))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** Business days is Airtable's figure (Mon–Fri, no holidays); this only words it. */
export function resolutionTimeLabel(days: number | null): string | null {
  if (days == null) return null;
  if (days <= 0) return 'Resolved the same day';
  return `Resolved in ${days} business day${days === 1 ? '' : 's'}`;
}
